import { computed, makeObservable } from 'mobx';

import { moment } from '@feathr/hooks';
import type { TConstraints, TOptionalConstraint } from '@feathr/rachis';

import { addressConstraints } from '../address';
import { EmailBaseCampaign } from './email_base';
import type { IMailrailBaseEmailCampaign } from './types';
import { CampaignState } from './types';

type PartialAttrs = Partial<IMailrailBaseEmailCampaign>;

const contactInfoConstraints: TConstraints<IMailrailBaseEmailCampaign> = Object.fromEntries(
  Object.entries(addressConstraints).map(([key, constraint]) => [
    `contact_info.${key}`,
    constraint,
  ]),
);

export abstract class MailrailBaseEmailCampaign<
  T extends IMailrailBaseEmailCampaign = IMailrailBaseEmailCampaign,
> extends EmailBaseCampaign<T> {
  public override get constraints(): TConstraints<IMailrailBaseEmailCampaign> {
    return {
      ...super.constraints,
      date_start: {
        datetime: (value: string, attributes: PartialAttrs): TOptionalConstraint => {
          if (!value) {
            return undefined;
          }
          if (
            attributes.state &&
            [CampaignState.Draft, CampaignState.Stopped].includes(attributes.state)
          ) {
            return {
              earliest: moment.utc().add(15, 'minutes'),
              message: `^Start time must be at least 15 minutes in the future`,
            };
          }
          return undefined;
        },
      },
      date_end: {
        datetime: (_: string, attributes: PartialAttrs): TOptionalConstraint => {
          if (attributes.date_start) {
            const dateStart = moment.utc(attributes.date_start, moment.ISO_8601);
            return {
              earliest: dateStart.add(23, 'hours').add(59, 'minutes'),
              message: '^End date/time must be at least 24 hours after start time',
            };
          }
          return undefined;
        },
        presence: {
          allowEmpty: false,
          message: '^End date/time cannot be empty',
        },
      },
      name: {
        presence: {
          allowEmpty: false,
        },
      },
      segments_excluded: {
        list: {
          presence: {
            allowEmpty: false,
            message: '^Finish selecting a group or remove the placeholder group.',
          },
        },
        duplicates: {
          maximum: 0,
          message: '^Groups must be unique.',
        },
      },
      segments_included: {
        list: {
          presence: {
            allowEmpty: false,
            message: '^Finish selecting a group or remove the placeholder group.',
          },
        },
        duplicates: {
          maximum: 0,
          message: '^Groups must be unique.',
        },
      },
      template_id: {
        presence: { allowEmpty: false, message: '^Choose a template' },
      },
      ...contactInfoConstraints,
    };
  }

  constructor(attributes: Partial<T>) {
    super(attributes);

    makeObservable(this);
  }

  public override getDefaults(): Partial<T> {
    return {
      ...super.getDefaults(),
      date_start: undefined,
      segments_excluded: [],
      segments_included: [],
    } as Partial<T>;
  }

  @computed
  public get editable(): boolean {
    /*
     * Because of limitations with mobx and subclassing, we cannot override editable
     * in SmartMailrailBaseEmailCampaign.
     */
    if (this.isMailrailAutoSend && this.isStopped) {
      // Prevent editing or re-running autosend campaigns that are stopped.
      // This is required for non-repeating autosends because of the TTL on the Valkey deduplication filter.
      // We should be able to remove this restriction if we implement some logic to dump the filter from
      // Valkey into a field on the campaign document when we stop the campaign, and push it back to Valkey
      // if the campaign is re-published.
      return false;
    }
    return true;
  }
}
